'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import type { ForgeArenaData } from '@/lib/services/forgeArenaService';

const HeadToHeadChart = dynamic(() => import('./HeadToHeadChart'), { ssr: false });

const periods = ['TODAY', '7 DAYS', '30 DAYS'] as const;
type Period = typeof periods[number];

interface HeadToHeadScoresProps {
  arenaData: ForgeArenaData | null;
  loading: boolean;
}

export default function HeadToHeadScores({ arenaData, loading }: HeadToHeadScoresProps) {
  const [period, setPeriod] = useState<Period>('TODAY');

  const scores: Record<Period, { theo: number; tracy: number }> = {
    'TODAY': {
      theo: arenaData?.todayScores.theo ?? 0,
      tracy: arenaData?.todayScores.tracy ?? 0,
    },
    '7 DAYS': {
      theo: arenaData?.weekScores.theo ?? 0,
      tracy: arenaData?.weekScores.tracy ?? 0,
    },
    '30 DAYS': {
      theo: arenaData?.monthScores.theo ?? 0,
      tracy: arenaData?.monthScores.tracy ?? 0,
    },
  };

  const { theo, tracy } = scores[period];
  const theoWins = theo >= tracy;

  return (
    <div className="card-forge rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-forge-label">Head-to-Head</div>
        <div className="flex gap-1">
          {periods.map((p) => (
            <button
              key={`period-${p}`}
              onClick={() => setPeriod(p)}
              className={`px-2.5 py-1 rounded text-2xs font-bold tracking-wider transition-all ${
                period === p
                  ? 'bg-primary/10 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground border border-transparent'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-24 bg-muted rounded-lg" />
            <div className="w-8 h-8 bg-muted rounded" />
            <div className="flex-1 h-24 bg-muted rounded-lg" />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4 mb-6">
          <div className={`flex-1 text-center p-4 rounded-lg border ${theoWins ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
            <div className="text-forge-label mb-1">THEO</div>
            <div className="font-tabular font-black text-5xl text-primary">{theo}</div>
            {theoWins && <div className="text-2xs text-primary mt-1 font-bold">▲ LEADING</div>}
          </div>
          <div className="text-center">
            <div className="text-muted-foreground font-bold text-lg">VS</div>
            <div className="text-xs text-muted-foreground mt-1">{Math.abs(theo - tracy)} pts</div>
          </div>
          <div className={`flex-1 text-center p-4 rounded-lg border ${!theoWins ? 'border-accent/30 bg-accent/5' : 'border-border'}`}>
            <div className="text-forge-label mb-1">TRACY</div>
            <div className="font-tabular font-black text-5xl text-accent">{tracy}</div>
            {!theoWins && <div className="text-2xs text-accent mt-1 font-bold">▲ LEADING</div>}
          </div>
        </div>
      )}

      <HeadToHeadChart weeklyScores={arenaData?.weeklyScores} />
    </div>
  );
}